import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import { Autocomplete, Box, CircularProgress, IconButton, Select, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { GetChatsByAtendimentoId, GetChatsByAtendimentoIdAndThreadIdWithPagination, SendChat } from '../../services/chat';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import Markdown from 'markdown-to-jsx';
import { set } from 'react-ga';

const novoChatSelecionado = {
    id: 'new',
    label: 'Novo Chat',
    date: new Date().toISOString(),
    threadId: 'new',
    assistenteId: '',
};

const perguntasExemplo = [
    'Crie um lista de medicamentos que o paciente está tomando.',
    'Liste todos os exames fisicos do paciente.',
    'Crie um pequeno resumo da cosulta.',
];

function ChatIA({ atendimento }) {
    const theme = useTheme();
    const [mensagens, setMensagens] = useState([]);
    const [novaMensagem, setNovaMensagem] = useState('');
    const [historicoChats, setHistoricoChats] = useState([]);
    const [chatSelecionado, setChatSelecionado] = useState(novoChatSelecionado);
    const [loadingMensagens, setLoadingMensagens] = useState(false);
    const [loadingSendMensagem, setLoadingSendMensagem] = useState(false);
    const containerMensagens = useRef(null);

    const historicoChatsOptions = useMemo(() => {
        const list = historicoChats
            .map((chat) => ({
                id: chat.threadId,
                label: dayjs(chat.dataCriacao).format('DD/MM/YY HH:mm'),
                date: chat.dataCriacao,
                threadId: chat.threadId,
                assistenteId: chat.assistenteId,
            }))
            .sort((a, b) => new Date(b.date) - new Date(a.date));
        return [novoChatSelecionado, ...list];
    }, [historicoChats]);

    useEffect(() => {
        init();
    }, []);

    function handleSelectChat(event, newValue) {
        setMensagens([]);
        if (!newValue) {
            setChatSelecionado(novoChatSelecionado);
        } else {
            setChatSelecionado(newValue);
            carregarMensagens(atendimento.id, newValue.id);
        }
        setTimeout(() => {
            try {
                containerMensagens.current.scrollTop = containerMensagens.current.scrollHeight;
            } catch (error) {
                console.error('Erro ao rolar para o final do container de mensagens:', error);
            }
        }, 500);
    }

    async function init() {
        try {
            var chats = await GetChatsByAtendimentoId(atendimento.id);
            setHistoricoChats(chats);
        } catch (error) {
            console.error('Erro ao carregar os chats:', error);
        }
    }

    async function carregarMensagens(atendimentoId, threadId, limite = 20, offset = 0) {
        if (!atendimentoId || !threadId) {
            console.error('AtendimentoId e ThreadId são necessários para carregar as mensagens');
            return;
        }
        try {
            setLoadingMensagens(true);
            const mensagensCarregadas = await GetChatsByAtendimentoIdAndThreadIdWithPagination(
                atendimentoId,
                threadId,
                limite, // Limite de mensagens a carregar
                offset, // Offset inicial
            );
            setMensagens((prevMensagens) => [...prevMensagens, ...mensagensCarregadas]);
        } catch (error) {
            console.error('Erro ao carregar as mensagens:', error);
        } finally {
            setLoadingMensagens(false);
        }
    }

    async function enviarMensagem(atendimentoId, threadId, assistenteId, novaMensagem) {
        if (!novaMensagem.trim()) {
            return toast.error('A mensagem não pode estar vazia.');
        }
        try {
            setLoadingSendMensagem(true);
            const novaMensagemEnviada = await SendChat(atendimentoId, threadId, assistenteId, novaMensagem);
            setMensagens((prevMensagens) => [
                ...prevMensagens,
                novaMensagemEnviada.chatUsuario,
                novaMensagemEnviada.chatAssistente,
            ]);
            setNovaMensagem('');
            setTimeout(() => {
                try {
                    containerMensagens.current.scrollTop = containerMensagens.current.scrollHeight;
                } catch (error) {
                    console.error('Erro ao rolar para o final do container de mensagens:', error);
                }
            }, 500);
            if (threadId === novoChatSelecionado.threadId) {
                setHistoricoChats((prevChats) => [
                    {
                        ...novaMensagemEnviada.chatAssistente,
                        atendimento: atendimento,
                    },
                    ...prevChats,
                ]);
                setChatSelecionado({
                    id: novaMensagemEnviada.chatAssistente.threadId,
                    label: dayjs(novaMensagemEnviada.chatAssistente.dataCriacao).format('DD/MM/YY HH:mm'),
                    date: novaMensagemEnviada.chatAssistente.dataCriacao,
                    threadId: novaMensagemEnviada.chatAssistente.threadId,
                    assistenteId: novaMensagemEnviada.chatAssistente.assistenteId,
                });
            }
        } catch (error) {
            console.error('Erro ao enviar a mensagem:', error);
        } finally {
            setLoadingSendMensagem(false);
        }
    }

    return (
        <Box
            flex={1}
            bgcolor={theme.palette.grey[100]}
            borderRadius={2}
            padding={2}
            display="flex"
            flexDirection="column"
            gap={2}
        >
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                <Typography variant="h6" color={theme.palette.primary.main} sx={{ flex: 1 }}>
                    Chat IA R1
                </Typography>
                <Autocomplete
                    size="small"
                    options={historicoChatsOptions}
                    value={chatSelecionado}
                    onChange={handleSelectChat}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => <TextField {...params} label="Chat Selecionado" />}
                    sx={{ minWidth: '220px' }}
                    renderOption={(props, option) => (
                        <Box
                            component="li"
                            {...props}
                            key={option.id}
                            display="flex"
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            width="100%"
                            gap={1}
                        >
                            {/* <Typography variant="caption" color={theme.palette.text.secondary}>
                                {dayjs(option.date).format('DD/MM/YY HH:mm')}
                            </Typography> */}
                            <Typography variant="caption" color={theme.palette.text.primary}>
                                {option.label}
                            </Typography>
                        </Box>
                    )}
                />
            </Box>
            {mensagens.length > 0 ? (
                <Box
                    ref={containerMensagens}
                    flex={1}
                    overflow="auto"
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    sx={{ scrollbarColor: `${theme.palette.primary.main} transparent`, scrollBehavior: 'smooth' }}
                >
                    {mensagens.map((mensagem) => (
                        <Box
                            key={mensagem.id}
                            display="flex"
                            flexDirection={mensagem.enviadoPeloUsuario ? 'row-reverse' : 'row'}
                            alignItems="center"
                            gap={1}
                        >
                            <Box
                                bgcolor={mensagem.enviadoPeloUsuario ? theme.palette.primary.main : theme.palette.grey[300]}
                                padding={1.5}
                                borderRadius={2}
                                maxWidth="70%"
                                minWidth={'50px'}
                                position={'relative'}
                                color={mensagem.enviadoPeloUsuario ? '#fff' : '#000'}
                                sx={{ paddingLeft: 2, paddingRight: 2 }}
                            >
                                <Markdown>{mensagem.mensagem}</Markdown>
                            </Box>
                        </Box>
                    ))}
                </Box>
            ) : (
                <Box display={'flex'} flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
                    <Typography variant="h6" color={theme.palette.text.secondary} textAlign="center">
                        Selecione um chat ou inicie uma nova conversa.
                    </Typography>
                    <Typography variant="body2" color={theme.palette.text.secondary} textAlign="center">
                        Você pode perguntar sobre o atendimento, como por exemplo:
                    </Typography>
                    <Box display={'flex'} flexDirection="column" sx={{ marginTop: 2 }}>
                        {perguntasExemplo.map((pergunta, index) => (
                            <Typography
                                key={`${index}-${pergunta}`}
                                variant="body2"
                                color={theme.palette.text.secondary}
                                textAlign="center"
                                sx={{ '&:hover': { color: theme.palette.primary.main, cursor: 'pointer' } }}
                                onClick={() => {
                                    if (loadingSendMensagem) toast.error('Aguarde o envio da mensagem atual.');
                                    setNovaMensagem(pergunta);
                                    enviarMensagem(
                                        atendimento.id,
                                        chatSelecionado.threadId,
                                        chatSelecionado.assistenteId,
                                        pergunta,
                                    );
                                }}
                            >
                                "{pergunta}"
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}
            <Box display={'flex'} alignItems={'center'} gap={1}>
                <TextField
                    variant="outlined"
                    multiline
                    maxRows={3}
                    placeholder="Digite sua mensagem aqui..."
                    fullWidth
                    value={novaMensagem}
                    onChange={(e) => setNovaMensagem(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (!loadingSendMensagem && novaMensagem.trim()) {
                                enviarMensagem(
                                    atendimento.id,
                                    chatSelecionado.threadId,
                                    chatSelecionado.assistenteId,
                                    novaMensagem,
                                );
                            }
                        }
                    }}
                    disabled={loadingSendMensagem}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                        },
                        '& .MuiInputBase-input': {
                            padding: '5px',
                        },
                        scrollbarColor: `${theme.palette.primary.main} transparent`,
                        scrollbarWidth: 'thin',
                    }}
                />
                {novaMensagem.length > 0 &&
                    (loadingSendMensagem ? (
                        <CircularProgress color="primary" />
                    ) : (
                        <IconButton
                            color="primary"
                            onClick={() =>
                                enviarMensagem(
                                    atendimento.id,
                                    chatSelecionado.threadId,
                                    chatSelecionado.assistenteId,
                                    novaMensagem,
                                )
                            }
                            disabled={loadingSendMensagem}
                        >
                            <SendIcon />
                        </IconButton>
                    ))}
            </Box>
        </Box>
    );
}

export default ChatIA;
